/**
 * Agent Portal Layout — sidebar dedicata all'agente.
 * Nascoste tutte le info admin: solo clienti/preventivi/ordini/provvigioni assegnati.
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAgentAuth } from '../../contexts/AgentAuthContext';
import {
  LayoutDashboard, Users, FileText, ShoppingBag, Wallet, LogOut, Briefcase, Package,
  Wrench, ListOrdered, Calculator, Palette, LayoutGrid,
} from 'lucide-react';

const AgentLayout = ({ children }) => {
  const { agentUser, agentToken, agentLogout, loading } = useAgentAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const strumentiActive = location.pathname.startsWith('/agent/strumenti');
  const [strumentiOpen, setStrumentiOpen] = useState(strumentiActive);
  const [pendingGraphics, setPendingGraphics] = useState(0);

  // Poll leggero del conteggio bozze "da validare" (files_ready) per badge sidebar
  useEffect(() => {
    if (!agentToken) return;
    let alive = true;
    const fetchCount = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agent/graphic-requests?status=files_ready`, {
          headers: { Authorization: `Bearer ${agentToken}` },
        });
        if (r.ok && alive) {
          const j = await r.json();
          setPendingGraphics((j.data || []).length);
        }
      } catch { /* ignore */ }
    };
    fetchCount();
    const iv = setInterval(fetchCount, 45000);
    return () => { alive = false; clearInterval(iv); };
  }, [agentToken, location.pathname]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Caricamento…</div>;
  }
  if (!agentToken || !agentUser) return <Navigate to="/agent/login" replace />;

  const nav = [
    { path: '/agent/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/agent/customers', label: 'I miei clienti', icon: Users },
    { path: '/agent/quotes', label: 'Preventivi', icon: FileText },
    { path: '/agent/orders', label: 'Ordini', icon: ShoppingBag },
    { path: '/agent/graphic-requests', label: 'Bozze grafiche', icon: Palette },
    { path: '/agent/samples', label: 'Campioni', icon: Package },
    { path: '/agent/commissions', label: 'Provvigioni', icon: Wallet },
    ...(agentUser?.agentInfo?.canManageLeads2 ? [
      { path: '/agent/leads2', label: 'LEAD 2.0', icon: LayoutGrid },
      { path: '/agent/leads2/todo', label: 'TO DO', icon: ListOrdered },
    ] : []),
  ];

  const strumentiSub = [
    { path: '/agent/strumenti/listino', label: 'Listino Prezzi', icon: ListOrdered },
    { path: '/agent/strumenti/calcolatore', label: 'Calcolatore Guadagni', icon: Calculator },
  ];

  const handleLogout = async () => {
    await agentLogout();
    navigate('/agent/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/40 to-white flex" data-testid="agent-layout">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-white/80 backdrop-blur border-r border-purple-100 flex flex-col">
        <div className="px-6 py-6 border-b border-purple-100">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-600 to-fuchsia-500 flex items-center justify-center text-white">
              <Briefcase className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-purple-900 leading-tight">Portale Agente</div>
              <div className="text-[10px] text-purple-500 uppercase tracking-wide">IlTuoAmaro</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            const showBadge = item.path === '/agent/graphic-requests' && pendingGraphics > 0;
            return (
              <Link key={item.path} to={item.path} data-testid={`agent-nav-${item.path.split('/').pop()}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? 'bg-purple-100 text-purple-800'
                    : 'text-gray-600 hover:bg-purple-50 hover:text-purple-700'
                }`}
              >
                <Icon className="w-4 h-4" /> <span className="flex-1">{item.label}</span>
                {showBadge && (
                  <span
                    data-testid="agent-nav-graphics-badge"
                    className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-fuchsia-600 text-white text-[10px] font-bold flex items-center justify-center animate-pulse"
                    title={`${pendingGraphics} bozza${pendingGraphics === 1 ? '' : 'e'} da validare`}
                  >
                    {pendingGraphics}
                  </span>
                )}
              </Link>
            );
          })}

          {/* Strumenti (collapsible) */}
          <button
            type="button"
            onClick={() => setStrumentiOpen((v) => !v)}
            data-testid="agent-nav-strumenti"
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              strumentiActive
                ? 'bg-purple-100 text-purple-800'
                : 'text-gray-600 hover:bg-purple-50 hover:text-purple-700'
            }`}
          >
            <Wrench className="w-4 h-4" /> Strumenti
            <span className="ml-auto text-xs">{strumentiOpen ? '▾' : '▸'}</span>
          </button>
          {strumentiOpen && (
            <div className="pl-6 mt-1 space-y-1">
              {strumentiSub.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`agent-nav-strumenti-${item.path.split('/').pop()}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      active
                        ? 'bg-purple-100 text-purple-800'
                        : 'text-gray-600 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" /> {item.label}
                  </Link>
                );
              })}
            </div>
          )}
        </nav>
        <div className="p-3 border-t border-purple-100">
          <div className="px-3 py-2 mb-2 text-xs text-gray-600">
            <div className="font-semibold text-gray-900 truncate">{agentUser.firstName} {agentUser.lastName}</div>
            <div className="truncate">{agentUser.email}</div>
            <div className="text-[10px] font-mono text-purple-600 mt-1">
              {agentUser.userType === 'sub-agent' ? 'SUB-AGENT' : 'AGENT'}
            </div>
          </div>
          <button onClick={handleLogout} data-testid="agent-logout-btn"
            className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50">
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-x-hidden">
        <div className="max-w-6xl mx-auto p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AgentLayout;
